import { TrophyOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type {
  IncidentListItem,
  IncidentOmpAssetContext,
  IncidentOmpProposal,
} from '@/app/services/incident/incident.service';
import { IncidentOwnerTagBadge } from '@/app/components/incident-overview/IncidentOwnerTagBadge';
import {
  hasMixedIncidentOwnership,
  resolveAssetOwnerTag,
} from '@/app/components/incident-overview/utils/incident-landlord.utils';
import { formatEuro } from '~/app/landing/sla/shared/utils/sla-format.utils';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text, Title } = Typography;

type Props = {
  incident: IncidentListItem;
};

export function IncidentOmpExpandedRow({ incident }: Props) {
  const { t } = useTranslation('incidents');
  const omp = incident.omp;

  if (!omp) {
    return null;
  }

  const showAssetOwnerTags = hasMixedIncidentOwnership(omp.assets);

  const proposalColumns: ColumnsType<IncidentOmpProposal> = [
    {
      title: t('omp.expand.oem', 'O&M partner'),
      dataIndex: 'oemName',
      key: 'oemName',
      render: (oemName: string, record) => (
        <Flex align="center" gap={8}>
          <Text strong={record.isRecommended}>{oemName}</Text>
          {record.isRecommended ? (
            <TrophyOutlined
              className="incident-omp-proposal-trophy"
              title={t('omp.expand.recommended', 'Recommended')}
              aria-label={t('omp.expand.recommended', 'Recommended')}
            />
          ) : null}
        </Flex>
      ),
    },
    {
      title: t('omp.expand.window', 'Intervention'),
      dataIndex: 'interventionLabel',
      key: 'interventionLabel',
    },
    {
      title: t('omp.expand.oemCost', 'O&M cost'),
      dataIndex: 'oemCost',
      key: 'oemCost',
      render: (value: number) => formatEuro(value),
    },
    {
      title: t('omp.expand.revenueLoss', 'Revenue loss'),
      dataIndex: 'revenueLoss',
      key: 'revenueLoss',
      render: (value: number) => formatEuro(value),
    },
    {
      title: t('omp.expand.slaPenalty', 'SLA penalty'),
      dataIndex: 'slaPenalty',
      key: 'slaPenalty',
      render: (value: number) => formatEuro(value),
    },
    {
      title: t('omp.expand.totalImpact', 'Total impact'),
      dataIndex: 'totalImpact',
      key: 'totalImpact',
      render: (value: number, record) => (
        <Text strong={record.isRecommended}>{formatEuro(value)}</Text>
      ),
    },
  ];

  return (
    <div className="sla-table-expanded-panel incident-omp-expanded-panel">
      <Title level={5} className="sla-table-expanded-panel__title">
        {t('omp.expand.title', 'Financial impact breakdown')}
      </Title>

      <EvimoTable<IncidentOmpProposal>
        variant="flush"
        className="incident-omp-proposals-table"
        columns={proposalColumns}
        dataSource={omp.proposals}
        pagination={false}
        rowKey={(row) => `${row.oemPartnerId}-${row.interventionWithinHours}`}
        size="small"
        rowClassName={(row) =>
          row.isRecommended ? 'incident-omp-proposal-row--recommended' : ''
        }
      />

      {omp.assets.length > 1 ? (
        <div style={{ marginTop: 20 }}>
          <Title level={5}>
            {t('omp.expand.perAsset', 'Per-asset breakdown')}
          </Title>
          <EvimoTable
            variant="flush"
            size="small"
            pagination={false}
            rowKey="assetId"
            dataSource={omp.assets}
            columns={[
              {
                title: t('omp.expand.asset', 'Asset'),
                dataIndex: 'assetName',
                key: 'assetName',
                render: (
                  name: string | null,
                  record: IncidentOmpAssetContext,
                ) => (
                  <Flex align="center" gap={8} wrap>
                    <Text>{name ?? record.assetId}</Text>
                    {showAssetOwnerTags ? (
                      <IncidentOwnerTagBadge
                        tag={resolveAssetOwnerTag(record)}
                      />
                    ) : null}
                  </Flex>
                ),
              },
              {
                title: t('omp.expand.dailyRevenue', 'Daily revenue'),
                dataIndex: 'dailyRevenue',
                key: 'dailyRevenue',
                render: (value: number) => formatEuro(value),
              },
              {
                title: t('omp.expand.slaPenaltyAccrued', 'SLA penalty accrued'),
                dataIndex: 'slaPenaltyAccrued',
                key: 'slaPenaltyAccrued',
                render: (value: number) => formatEuro(value),
              },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}
